import Input from '../ui/Input';
import { formatExample, matchesFormat } from './FieldEditor';
import Select from '../ui/Select';

/**
 * Renders a single user-defined dynamic field — used by workflow steps (the /workflow
 * "Edit step" modal) and nationality masters (the Nationalities master page).
 * `value`/`onChange` speak the raw answer shape: a string for text/number/date/radio/select,
 * an array for checkbox/multiselect.
 */
export default function DynamicField({ field, value, onChange }) {
  // A read-only link to another step's field: show the same control, but disabled.
  if (field.linked_step_field && !field.linked_step_field.updatable) {
    return (
      <fieldset disabled title="Copied from another step's field" className="opacity-70">
        <DynamicFieldControl field={field} value={value} onChange={() => {}} />
      </fieldset>
    );
  }

  return <DynamicFieldControl field={field} value={value} onChange={onChange} />;
}

function DynamicFieldControl({ field, value, onChange }) {
  // Mirrors a fixed registration field — the value comes from the candidate record, not staff.
  if (field.linked_registration_field) {
    return <Input value={value || ''} readOnly disabled title="Copied from the candidate's registration data" />;
  }

  if (field.type === 'text') {
    const format = field.validation?.format;
    if (!format) return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} />;

    const invalid = !!value && !matchesFormat(format, value);
    return (
      <div>
        <Input value={value || ''} placeholder={formatExample(format)} onChange={(e) => onChange(e.target.value)} />
        <p className={`mt-1 text-xs ${invalid ? 'text-red-600' : 'text-gray-400'}`}>
          {invalid ? `Doesn't match the required format — e.g. ${formatExample(format)}` : `Format: e.g. ${formatExample(format)}`}
        </p>
      </div>
    );
  }

  if (field.type === 'number') {
    return <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }

  if (field.type === 'date') {
    return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }

  if (field.type === 'time') {
    return <Input type="time" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }

  const options = field.options || [];

  if (field.type === 'radio') {
    return (
      <div className="space-y-1">
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-gray-700">
            <input type="radio" name={field.id} checked={value === opt} onChange={() => onChange(opt)} />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <Select value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Select>
    );
  }

  const selected = value || [];
  const toggle = (opt, checked) => onChange(checked ? selected.filter((o) => o !== opt) : [...selected, opt]);

  if (field.type === 'checkbox') {
    return (
      <div className="space-y-1">
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt, selected.includes(opt))} />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  // multiselect
  return (
    <Select multiple value={selected} onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))} className="h-auto">
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </Select>
  );
}
